/**
 * Tutor Service
 * Handles tutor-related operations with Firebase
 */

import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs, 
  getDoc,
  doc,
  startAfter
} from 'firebase/firestore';
import { db } from '../../../services/firebase/firebase';

/**
 * Get approved tutors
 * 
 * @param {Object} filters - Filter options
 * @param {number} limitCount - Number of tutors to retrieve
 * @param {Object} lastDoc - Last document for pagination
 * @returns {Promise<Object>} Object containing tutors array and lastDoc
 */
const getApprovedTutors = async (filters = {}, limitCount = 12, lastDoc = null) => {
  try {
    let tutorQuery = query(
      collection(db, 'tutorApplications'),
      where('status', '==', 'approved'),
      orderBy('reviewedAt', 'desc')
    );

    // Add filters
    if (filters.university) {
      tutorQuery = query(
        collection(db, 'tutorApplications'),
        where('status', '==', 'approved'),
        where('university', '==', filters.university),
        orderBy('reviewedAt', 'desc')
      );
    }

    // Add pagination if lastDoc is provided
    if (lastDoc) {
      tutorQuery = query(tutorQuery, startAfter(lastDoc));
    }

    // Add limit
    tutorQuery = query(tutorQuery, limit(limitCount));

    const snapshot = await getDocs(tutorQuery);
    
    const tutors = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      submittedAt: doc.data().submittedAt?.toDate(),
      reviewedAt: doc.data().reviewedAt?.toDate()
    }));

    // Get the last document for pagination
    const newLastDoc = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;

    return {
      tutors,
      lastDoc: newLastDoc,
      hasMore: snapshot.docs.length === limitCount
    };
  } catch (error) {
    console.error('Error getting approved tutors:', error);
    throw error;
  }
};

/**
 * Search tutors by course code
 * 
 * @param {string} courseCode - Course code to search for
 * @param {number} limitCount - Number of tutors to retrieve
 * @returns {Promise<Array>} Array of tutor objects
 */
const searchTutorsByCourse = async (courseCode, limitCount = 12) => {
  try {
    const tutorQuery = query(
      collection(db, 'tutorApplications'),
      where('status', '==', 'approved'),
      orderBy('reviewedAt', 'desc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(tutorQuery);
    
    // Filter tutors who have the specific course
    const tutors = snapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data(),
        submittedAt: doc.data().submittedAt?.toDate(),
        reviewedAt: doc.data().reviewedAt?.toDate()
      }))
      .filter(tutor => 
        tutor.courses?.some(course => 
          course.courseCode?.toLowerCase().includes(courseCode.toLowerCase())
        )
      );

    return tutors;
  } catch (error) {
    console.error('Error searching tutors by course:', error);
    throw error;
  }
};

/**
 * Get tutor by ID
 * 
 * @param {string} tutorId - Tutor application ID
 * @returns {Promise<Object|null>} Tutor object or null
 */
const getTutorById = async (tutorId) => {
  try {
    const tutorRef = doc(db, 'tutorApplications', tutorId);
    const tutorSnap = await getDoc(tutorRef);
    
    if (!tutorSnap.exists()) {
      return null;
    }

    const tutorData = tutorSnap.data();
    
    // Only return if approved
    if (tutorData.status !== 'approved') {
      return null;
    }

    return {
      id: tutorSnap.id,
      ...tutorData,
      submittedAt: tutorData.submittedAt?.toDate(),
      reviewedAt: tutorData.reviewedAt?.toDate()
    };
  } catch (error) {
    console.error('Error getting tutor by ID:', error);
    throw error;
  }
};

/**
 * Get unique universities from approved tutors
 * 
 * @returns {Promise<Array>} Array of university names
 */
const getUniversities = async () => {
  try {
    const tutorQuery = query(
      collection(db, 'tutorApplications'),
      where('status', '==', 'approved')
    );

    const snapshot = await getDocs(tutorQuery);
    
    const universities = new Set();
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      if (data.university) {
        universities.add(data.university);
      }
    });

    return Array.from(universities).sort();
  } catch (error) {
    console.error('Error getting universities:', error);
    throw error;
  }
};

/**
 * Get unique course codes from approved tutors
 * 
 * @returns {Promise<Array>} Array of course codes
 */
const getCourseCodes = async () => {
  try {
    const tutorQuery = query(
      collection(db, 'tutorApplications'),
      where('status', '==', 'approved')
    );

    const snapshot = await getDocs(tutorQuery);
    
    const courseCodes = new Set();
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      if (data.courses && Array.isArray(data.courses)) {
        data.courses.forEach(course => {
          if (course.courseCode) {
            courseCodes.add(course.courseCode);
          }
        });
      }
    });

    return Array.from(courseCodes).sort();
  } catch (error) {
    console.error('Error getting course codes:', error);
    throw error;
  }
};

/**
 * Get tutors by university
 * 
 * @param {string} university - University name
 * @param {number} limitCount - Number of tutors to retrieve
 * @returns {Promise<Array>} Array of tutor objects
 */
const getTutorsByUniversity = async (university, limitCount = 12) => {
  try {
    const tutorQuery = query(
      collection(db, 'tutorApplications'),
      where('status', '==', 'approved'),
      where('university', '==', university),
      orderBy('reviewedAt', 'desc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(tutorQuery);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      submittedAt: doc.data().submittedAt?.toDate(),
      reviewedAt: doc.data().reviewedAt?.toDate()
    }));
  } catch (error) {
    console.error('Error getting tutors by university:', error);
    throw error;
  }
};

export default {
  getApprovedTutors,
  searchTutorsByCourse,
  getTutorById,
  getUniversities,
  getCourseCodes,
  getTutorsByUniversity
};